import React, { useContext } from 'react';
import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';
import get from 'lodash/get';

import Input from '../../../components/Input';
import { REJECT_HUB_COUNTER, RejectHubCounterMutation } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';
import UserContext from '../../../contexts/UserContext';

interface RejectHubCounterModalProps {
  isOpen: boolean;
  onClose: () => void;
  dropPointId: string;
  refetch: () => Promise<any> | void;
  refetchState?: () => Promise<any> | void;
}

const RejectHubCounterSchema = Yup.object().shape({
  reason: Yup.string().required('Du musst einen Ablehnungsgrund eingeben'),
});

const RejectHubCounterModal = ({ isOpen, onClose, dropPointId, refetch, refetchState }: RejectHubCounterModalProps) => {
  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const toast = useCustomToast();
  const [rejectHubCounter] = useMutation<RejectHubCounterMutation>(REJECT_HUB_COUNTER);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Hub Counter ablehnen</ModalHeader>
        <Formik
          initialValues={{ reason: '' }}
          validationSchema={RejectHubCounterSchema}
          onSubmit={async ({ reason }) => {
            try {
              toggleSpinner(true);
              await rejectHubCounter({
                variables: {
                  dropPointId,
                  reason: reason.trim(),
                  internalUserId: internalUser.id,
                },
              });
              await Promise.all([Promise.resolve(refetch()), Promise.resolve(refetchState?.())]);
              toast({
                title: 'Erfolg!',
                description: 'Hub Counter wurde abgelehnt.',
                status: 'success',
              });
              onClose();
            } catch (error) {
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Fehler beim Ablehnen.'),
                status: 'error',
              });
            } finally {
              toggleSpinner(false);
            }
          }}
        >
          <Form>
            <ModalBody>
              <Text fontSize="sm" mb="4">
                Bitte dokumentiere den Grund der Ablehnung, damit das Support-Team den Fall nachvollziehen kann.
              </Text>
              <Input name="reason" label="Ablehnungsgrund" />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                Abbrechen
              </Button>
              <Button ml="4" size="sm" colorScheme="brand" type="submit">
                Ablehnen
              </Button>
            </ModalFooter>
          </Form>
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default RejectHubCounterModal;
